import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { BRANCH_META } from "@/lib/branches";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Rocket, Sparkles, X, Plus, Trash2, Send, ArrowRight, Lightbulb, Users } from "lucide-react";

export default function ProjectsNew() {
    const nav = useNavigate();
    const [disciplines, setDisciplines] = useState([]);
    const [title, setTitle] = useState("");
    const [summary, setSummary] = useState("");
    const [discipline, setDiscipline] = useState("");
    const [org, setOrg] = useState("");
    const [website, setWebsite] = useState("");
    const [tagInput, setTagInput] = useState("");
    const [tags, setTags] = useState([]);
    const [milestones, setMilestones] = useState([""]);
    const [submitting, setSubmitting] = useState(false);

    // AI similarity state
    const [similar, setSimilar] = useState([]);
    const [checkingSim, setCheckingSim] = useState(false);
    const [simError, setSimError] = useState(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        api.get("/atlas/disciplines").then((r) => setDisciplines(r.data));
    }, []);

    // Debounced AI similarity check whenever title/summary/discipline changes meaningfully
    useEffect(() => {
        if (title.trim().length < 5 || summary.trim().length < 15) {
            setSimilar([]);
            return;
        }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setCheckingSim(true);
            setSimError(null);
            try {
                const { data } = await api.post("/projects/similar", {
                    title: title.trim(),
                    summary: summary.trim(),
                    discipline: discipline || null,
                });
                setSimilar(data.candidates || []);
            } catch (e) {
                setSimError("Couldn't run similarity check.");
            } finally {
                setCheckingSim(false);
            }
        }, 1200);
        return () => clearTimeout(debounceRef.current);
    }, [title, summary, discipline]);

    const addTag = () => {
        const t = tagInput.trim().toLowerCase();
        if (!t || tags.includes(t) || tags.length >= 10) return;
        setTags((prev) => [...prev, t]);
        setTagInput("");
    };

    const setMilestone = (i, v) => setMilestones((prev) => prev.map((m, idx) => (idx === i ? v : m)));
    const addMilestone = () => setMilestones((prev) => (prev.length < 8 ? [...prev, ""] : prev));
    const removeMilestone = (i) => setMilestones((prev) => prev.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !summary.trim() || !discipline) {
            toast.error("Add title, summary and pick a discipline.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/projects", {
                title,
                summary,
                discipline,
                org: org || null,
                website: website || null,
                tags,
                milestones: milestones.map((m) => m.trim()).filter(Boolean),
            });
            toast.success("Project launched.");
            nav(`/projects/${data.project.slug}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't post that project.";
            toast.error(String(msg));
        } finally {
            setSubmitting(false);
        }
    };

    const disciplineMeta = disciplines.find((d) => d.slug === discipline);
    const meta = disciplineMeta ? BRANCH_META[disciplineMeta.branch] : BRANCH_META.business;

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-6xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-projects">
                    <Rocket className="w-4 h-4" /> All projects
                </Link>

                <div className="grid lg:grid-cols-3 gap-6 mt-4">
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="lg:col-span-2 crystal rounded-3xl p-6 sm:p-8"
                    >
                        <p className="chip" style={{ background: meta.soft, color: meta.text }}>
                            <Rocket className="w-3 h-3" /> New project
                        </p>
                        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-900 mt-2 leading-none">
                            Plant your flag.
                        </h1>
                        <p className="text-slate-600 mt-2 text-sm">
                            As you type, the AI is looking for anyone already working on this — so you can join forces
                            instead of duplicating effort.
                        </p>

                        <div className="mt-6 space-y-4">
                            <div>
                                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">Project title</label>
                                <input
                                    className="field-input"
                                    placeholder="e.g. Coral bleaching early warning network"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    maxLength={140}
                                    required
                                    data-testid="project-title"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">
                                    Summary — what it does & the problem it solves
                                </label>
                                <textarea
                                    className="field-input min-h-[140px]"
                                    placeholder="A few sentences on what you're building, why, and who could join. Be specific — this is what the AI uses to find your collaborators."
                                    value={summary}
                                    onChange={(e) => setSummary(e.target.value)}
                                    maxLength={1200}
                                    required
                                    data-testid="project-summary"
                                />
                                <div className="text-[10px] text-slate-400 mt-1">{summary.length}/1200</div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">Discipline</label>
                                    <select
                                        className="field-input"
                                        value={discipline}
                                        onChange={(e) => setDiscipline(e.target.value)}
                                        required
                                        data-testid="project-discipline"
                                    >
                                        <option value="">Pick one…</option>
                                        {disciplines.map((d) => (
                                            <option key={d.slug} value={d.slug}>
                                                {BRANCH_META[d.branch]?.name} · {d.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">Organization</label>
                                    <input
                                        className="field-input"
                                        placeholder="Independent, your company, university…"
                                        value={org}
                                        onChange={(e) => setOrg(e.target.value)}
                                        maxLength={120}
                                        data-testid="project-org"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">Website (optional)</label>
                                <input
                                    className="field-input"
                                    placeholder="https://…"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    maxLength={300}
                                    data-testid="project-website"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">
                                    Tags <span className="normal-case font-normal text-slate-400">— up to 10</span>
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        className="field-input"
                                        placeholder="add a tag then press Enter"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                                        data-testid="project-tag-input"
                                    />
                                    <button type="button" onClick={addTag} className="arcade-btn" data-variant="ghost" data-testid="project-tag-add">
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                                {tags.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {tags.map((t) => (
                                            <span key={t} className="chip" style={{ background: meta.soft, color: meta.text }}>
                                                #{t}
                                                <button type="button" onClick={() => setTags((prev) => prev.filter((x) => x !== t))} className="ml-1 opacity-70 hover:opacity-100">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">
                                    Milestones <span className="normal-case font-normal text-slate-400">— up to 8</span>
                                </label>
                                <div className="space-y-2">
                                    {milestones.map((m, i) => (
                                        <div key={i} className="flex gap-2">
                                            <input
                                                className="field-input"
                                                placeholder={`Milestone ${i + 1}`}
                                                value={m}
                                                onChange={(e) => setMilestone(i, e.target.value)}
                                                maxLength={120}
                                                data-testid={`project-milestone-${i}`}
                                            />
                                            {milestones.length > 1 && (
                                                <button type="button" onClick={() => removeMilestone(i)} className="arcade-btn" data-variant="ghost">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {milestones.length < 8 && (
                                        <button type="button" onClick={addMilestone} className="arcade-btn" data-variant="ghost" data-testid="project-milestone-add">
                                            <Plus className="w-4 h-4" /> Add milestone
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <Link to="/projects" className="arcade-btn" data-variant="ghost">Cancel</Link>
                            <button className="arcade-btn" data-variant={disciplineMeta?.branch || "primary"} disabled={submitting} data-testid="project-submit">
                                <Rocket className="w-4 h-4" /> {submitting ? "Launching…" : "Launch project"}
                            </button>
                        </div>
                    </motion.form>

                    {/* AI similarity panel */}
                    <motion.aside
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="crystal rounded-3xl p-5 h-fit sticky top-24"
                        data-testid="similarity-panel"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4" color={BRANCH_META.humanities.color} />
                            <h2 className="font-display font-bold text-lg tracking-tight text-slate-900">
                                Similar work
                            </h2>
                        </div>
                        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                            Real projects your effort could join, not duplicate. Updates as you type.
                        </p>

                        {checkingSim && (
                            <div className="text-xs text-slate-500 flex items-center gap-2">
                                <div className="w-3 h-3 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
                                Scanning…
                            </div>
                        )}
                        {simError && !checkingSim && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
                                {simError}
                            </div>
                        )}
                        {!checkingSim && !simError && similar.length === 0 && (
                            <div className="text-xs text-slate-500 leading-relaxed">
                                {(title.length < 5 || summary.length < 15)
                                    ? "Add a title and 1-2 sentences of summary to start finding potential collaborators."
                                    : "No overlapping projects yet — you may be first on this frontier."}
                            </div>
                        )}
                        <AnimatePresence>
                            {similar.map((s, i) => (
                                <motion.div
                                    key={s.slug}
                                    initial={{ opacity: 0, x: 6 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="rounded-2xl border border-slate-200 bg-white/80 p-3 mb-2 last:mb-0"
                                    data-testid={`similar-${i}`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <span
                                            className="text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded"
                                            style={{
                                                background: s.overlap_strength === "strong" ? "#3EB489" : s.overlap_strength === "moderate" ? "#FFC107" : "#94A3B8",
                                                color: "#0F172A",
                                            }}
                                        >
                                            {s.overlap_strength || "match"}
                                        </span>
                                        {s.org && <span className="text-[10px] text-slate-400 uppercase font-bold">{s.org}</span>}
                                    </div>
                                    <Link to={`/projects/${s.slug}`} target="_blank" className="font-display font-bold text-slate-900 text-sm hover:text-humanities transition-colors">
                                        {s.title}
                                        <ArrowRight className="w-3 h-3 inline ml-1" />
                                    </Link>
                                    <p className="text-xs text-slate-700 mt-1 leading-relaxed">
                                        <Lightbulb className="w-3 h-3 inline mr-1" style={{ color: BRANCH_META.arts.color }} />
                                        {s.why_connect}
                                    </p>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.aside>
                </div>
            </div>
        </div>
    );
}
